import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Collapsible,
  SearchField,
  Row,
  Col,
  ModalDialog,
  ActionRow,
  Hyperlink,
} from '@openedx/paragon';
import { getConfig } from '@edx/frontend-platform';
import { Close } from '@openedx/paragon/icons';
import { logStudioHomeError } from '../../../logging';
import { RequestStatus } from '../../../../data/constants';
import {
  getSavingStatuses, getCompanyCatalog, getLegacyLicenses, getFullCourseList, getLoadingStatuses, getPrograms,
} from '../../../data/selectors';
import {
  updateCompanyLicenseQuantities,
  removeCompanyLicenses,
} from '../../../data/thunks';
import { getActiveVersionCourseId, getCatalogSkillVersions } from './catalogUtils';
// Temporarily comment out external form components to fix modal issue
// import AddLicenseForm from './AddLicenseForm';
// import EditLicenseForm from './EditLicenseForm';

interface Company {
  company_name: string;
  friendly_name: string;
  edx_course_org?: string;
}

interface LicenseItem {
  id: string;
  name: string;
  type: 'legacy_bundle' | 'catalog_course' | 'program';
  available: number | null; // null means unlimited
  used: number;
  pk_id?: number;
  /** Full versioned course_id for the active run. */
  activeVersionId?: string;
  /** All version records from the v2 catalog entry */
  versions?: Array<{ version: string; used: number; active: boolean }>;
  courseList?: string[];
  isCollapsed?: boolean;
}

interface SelectedCourse {
  courseId: string;
  quantity: number | null;
}

interface Props {
  company: Company | null;
}

const LicenseManagement: React.FC<Props> = ({ company }) => {
  const dispatch = useDispatch();
  const savingStates = useSelector(getSavingStatuses);
  const companyCatalog = useSelector(getCompanyCatalog);
  const legacyLicenses = useSelector(getLegacyLicenses);
  const programs = useSelector(getPrograms);
  const courses = useSelector(getFullCourseList);
  const { fullCourseListLoadingStatus } = useSelector(getLoadingStatuses);
  const isLoadingCourses = fullCourseListLoadingStatus === RequestStatus.IN_PROGRESS;

  const [licenses, setLicenses] = useState<LicenseItem[]>([]);
  const [filteredLicenses, setFilteredLicenses] = useState<LicenseItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLicense, setEditingLicense] = useState<LicenseItem | null>(null);
  const [modalError, setModalError] = useState('');
  const [modalSuccess, setModalSuccess] = useState('');
  const [operationError, setOperationError] = useState('');
  const [editQuantity, setEditQuantity] = useState<string>('');
  const [selectedCourses, setSelectedCourses] = useState<SelectedCourse[]>([]);
  const [courseSearchQuery, setCourseSearchQuery] = useState('');
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [courseFilter, setCourseFilter] = useState<'company' | 'uopx' | 'both' | 'all'>('company');
  const [selectedForRemoval, setSelectedForRemoval] = useState<Set<string>>(new Set());
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);
  // Tracks which skill IDs have their versions panel expanded
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());

  const toggleVersionsExpanded = (licenseId: string) => {
    setExpandedVersions(prev => {
      const next = new Set(prev);
      if (next.has(licenseId)) { next.delete(licenseId); } else { next.add(licenseId); }
      return next;
    });
  };

  // Combine data from split sources
  const selectedCompanyLicenses = React.useMemo(() => ({
    legacyBundles: legacyLicenses?.bundles || [],
    catalog: companyCatalog?.catalog || {},
    programs: programs || [],
    company: companyCatalog?.company || {},
    skillSelect: companyCatalog?.skill_select || null,
  }), [companyCatalog, legacyLicenses, programs]);

  // Loading states
  const isLoading = savingStates.fetchCompanyCatalogStatus === RequestStatus.IN_PROGRESS;
  const isAdding = savingStates.addLicensesSavingStatus === RequestStatus.IN_PROGRESS;
  const isRemoving = savingStates.removeLicensesSavingStatus === RequestStatus.IN_PROGRESS;
  const isUpdating = savingStates.updateLicensesSavingStatus === RequestStatus.IN_PROGRESS;

  // Get company org from catalog data
  const companyOrg = companyCatalog?.company?.edx_course_org || '';

  /**
   * Extract organization code from course ID
   * Format: course-v1:ORG+COURSE+RUN
   */
  const extractOrgFromCourseId = (courseId: string): string => {
    const match = courseId.match(/^course-v1:([^+]+)\+/);
    return match ? match[1] : '';
  };

  /**
   * Filter courses based on selected filter
   */
  const filterCoursesByOrg = (courseList: typeof courses) => {
    if (courseFilter === 'all') {
      return courseList;
    }

    return courseList.filter(course => {
      const courseOrg = extractOrgFromCourseId(course.courseId).toLowerCase();
      const companyOrgLower = companyOrg.toLowerCase();

      if (courseFilter === 'company') {
        return courseOrg === companyOrgLower;
      } if (courseFilter === 'uopx') {
        return courseOrg === 'uopx';
      } if (courseFilter === 'both') {
        return courseOrg === companyOrgLower || courseOrg === 'uopx';
      }

      return true;
    });
  };

  // Process license data when it changes
  useEffect(() => {
    if (selectedCompanyLicenses) {
      // The helper is declared below with access to the component's course data.
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      const processedLicenses = processLicenseData(selectedCompanyLicenses);
      setLicenses(processedLicenses);
      setFilteredLicenses(processedLicenses);
    }
  }, [selectedCompanyLicenses]);

  // Filter licenses based on search
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredLicenses(licenses);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = licenses.filter((license) => {
        // Match top-level name or ID
        if (license.name.toLowerCase().includes(term) || license.id.toLowerCase().includes(term)) {
          return true;
        }
        // For standalone catalog courses, also match the resolved display name
        if (license.type === 'catalog_course') {
          const displayIdForSearch = license.activeVersionId || license.id;
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          const displayName = getCourseDisplayName(displayIdForSearch) || getCourseDisplayName(license.id);
          if (displayName?.toLowerCase().includes(term)) { return true; }
        }
        // For bundles and programs, match any contained course by ID or display name.
        // This lets staff find a pathway by searching for a skill inside it.
        if (license.courseList && license.courseList.length > 0) {
          return license.courseList.some((courseId) => {
            if (courseId.toLowerCase().includes(term)) { return true; }
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            const displayName = getCourseDisplayName(courseId);
            return displayName?.toLowerCase().includes(term) ?? false;
          });
        }
        return false;
      });
      setFilteredLicenses(filtered);
    }
  }, [searchTerm, licenses]);

  /**
   * Get course display name from course ID
   */
  const getCourseDisplayName = (courseId: string): string | null => {
    const course = courses.find(c => c.courseId === courseId);
    return course?.displayName || null;
  };

  /**
   * Build Studio course authoring URL for a course ID
   */
  const getCourseUrl = (courseId: string): string => {
    const studioBaseUrl = getConfig().STUDIO_BASE_URL;
    return `${studioBaseUrl}/course/${courseId}`;
  };

  /**
   * Process raw license data from API into unified format
   */
  function processLicenseData(rawData: any): LicenseItem[] {
    const processedItems: LicenseItem[] = [];
    const {
      legacyBundles = [], catalog = {}, programs: catalogPrograms = [],
    } = rawData;

    // Process legacy bundles
    legacyBundles.forEach((bundle: any) => {
      processedItems.push({
        id: bundle.license,
        name: bundle.license,
        type: 'legacy_bundle',
        available: bundle.available,
        used: bundle.used,
        courseList: bundle.course_list || [],
        isCollapsed: true,
      });
    });

    // Process company catalog courses and pathways
    // In v2 the catalog contains both skill entries (type="skill") and pathway entries (type="pathway").
    Object.keys(catalog).forEach((courseId: string) => {
      const course = catalog[courseId];
      const isPathway = course.type === 'pathway';

      if (isPathway) {
        // Pathway entry — resolve display name from programs list by item_id
        const meta = catalogPrograms.find((p: any) => p.item_id === courseId);
        processedItems.push({
          id: courseId,
          name: meta?.name || courseId,
          type: 'program',
          available: course.available,
          used: course.used || 0,
          pk_id: course.id,
          // Prefer children from the catalog entry; fall back to the program record's
          // course_list for pathways whose catalog entry was created before children
          // were populated by the signal.
          courseList: (course.children && course.children.length > 0)
            ? course.children
            : (meta?.course_list || []),
          isCollapsed: true,
        });
        return;
      }

      // Standalone course — check if this course is part of a program that does NOT
      // yet have its own catalog entry (legacy programs without a catalog pathway entry).
      const program = catalogPrograms.find((p: any) => p.course_list?.includes(courseId) && !catalog[p.item_id]);

      if (!program) {
        const versions = getCatalogSkillVersions(course);
        const activeVersionId = getActiveVersionCourseId(courseId, course);

        processedItems.push({
          id: courseId,
          name: activeVersionId,
          type: 'catalog_course',
          available: course.available,
          used: course.used || 0,
          pk_id: course.id,
          activeVersionId,
          versions,
          courseList: [courseId],
          isCollapsed: true,
        });
      }
    });

    // Process legacy programs/pathways that don't yet have a catalog entry.
    // Programs with a catalog entry are already rendered above from the catalog loop.
    catalogPrograms.forEach((program: any) => {
      if (catalog[program.item_id]) {
        return; // already rendered from the catalog pathway entry above
      }
      if (!program.course_list || program.course_list.length === 0) {
        return; // Skip empty programs
      }

      // Calculate totals from individual catalog courses
      let totalUsed = 0;
      let totalAvailable = 0;
      let hasUnlimited = false;
      let hasValidCourses = false;

      program.course_list.forEach((courseId: string) => {
        const courseData = catalog[courseId];
        if (courseData) {
          hasValidCourses = true;
          totalUsed += courseData.used || 0;

          if (courseData.available === null) {
            hasUnlimited = true; // At least one course has unlimited
          } else {
            totalAvailable += courseData.available || 0;
          }
        }
      });

      // Only add programs that have valid courses in the catalog
      if (hasValidCourses) {
        processedItems.push({
          id: program.item_id,
          name: program.name,
          type: 'program',
          available: hasUnlimited ? null : totalAvailable, // null if any course is unlimited
          used: totalUsed,
          courseList: program.course_list || [],
          isCollapsed: true,
        });
      }
    });

    return processedItems;
  }

  /**
   * Toggle collapsible state for a license
   */
  const toggleCollapse = (licenseId: string) => {
    setLicenses(prev => prev.map(license => (license.id === licenseId
      ? { ...license, isCollapsed: !license.isCollapsed }
      : license)));
  };

  /**
   * Handle adding new licenses
   */
  /**
   * Handle removing licenses
   */
  const handleRemoveLicense = async (license: LicenseItem) => {
    if (!company) { return; }

    // Clear any existing error
    setOperationError('');

    try {
      const isLegacy = license.type === 'legacy_bundle';
      const removeData = isLegacy ? [license.name] : [license.pk_id];

      await dispatch(removeCompanyLicenses(
        company.company_name,
        removeData,
        isLegacy,
      ));
    } catch (error) {
      logStudioHomeError('cmp_license_remove_failed', error);
      setOperationError(`Failed to remove license "${license.name}". Please try again.`);
    }
  };

  /**
   * Handle batch removing selected licenses
   */
  const handleBatchRemoveLicenses = async () => {
    if (!company || selectedForRemoval.size === 0) { return; }

    // Clear any existing error
    setOperationError('');

    try {
      // Convert Set of license IDs → array of PKs for v2 DELETE endpoint
      const licenseIdsToRemove = Array.from(selectedForRemoval)
        .map(id => licenses.find(l => l.id === id)?.pk_id)
        .filter(Boolean);

      await dispatch(removeCompanyLicenses(
        company.company_name,
        licenseIdsToRemove,
        false, // isLegacy=false for both catalog courses and catalog pathways
      ));

      // Clear selection after successful removal
      setSelectedForRemoval(new Set());
      setShowRemoveConfirmModal(false);
    } catch (error) {
      logStudioHomeError('cmp_licenses_remove_failed', error);
      setOperationError('Failed to remove selected licenses. Please try again.');
      setShowRemoveConfirmModal(false);
    }
  };

  /**
   * Toggle selection for a license
   */
  const toggleLicenseSelection = (licenseId: string) => {
    const newSelection = new Set(selectedForRemoval);
    if (newSelection.has(licenseId)) {
      newSelection.delete(licenseId);
    } else {
      newSelection.add(licenseId);
    }
    setSelectedForRemoval(newSelection);
  };

  /**
   * Handle updating license quantities
   */
  const handleUpdateQuantity = async (license: LicenseItem, quantityDelta: number | null) => {
    if (!company) { return; }

    try {
      setModalError(''); // Clear any existing errors
      const isLegacy = license.type === 'legacy_bundle';

      if (isLegacy) {
        // Legacy system receives delta directly - endpoint handles the math
        const updateData = [{
          license: license.name,
          available: quantityDelta, // Direct delta - endpoint handles the math
          course_list: license.courseList,
          used: license.used,
        }];

        await dispatch(updateCompanyLicenseQuantities(
          company.company_name,
          updateData,
          isLegacy,
        ));
      } else {
        // For catalog system, validate here but let thunk handle fetching current and calculating
        const currentAvailable = license.available === null ? 0 : license.available;
        const projectedNewValue = quantityDelta ? currentAvailable + quantityDelta : quantityDelta;
        // Frontend validation
        if (projectedNewValue !== null && projectedNewValue < 0) {
          setModalError(`New available quantity (${projectedNewValue}) cannot be less than zero.`);
          return;
        }

        // Send delta to thunk - it will fetch current values and calculate absolute values
        const updateData = [{
          course_id: license.id,
          delta: quantityDelta, // Send delta - thunk will calculate absolute value
        }];

        await dispatch(updateCompanyLicenseQuantities(
          company.company_name,
          updateData,
          isLegacy,
        ));
      }

      // Close modal and clear states on success
      setShowEditModal(false);
      setEditingLicense(null);
      setEditQuantity('');
      setSelectedCourses([]);
      setShowAddModal(false);
    } catch (error) {
      logStudioHomeError('cmp_license_quantity_update_failed', error);
      setModalError('Failed to update license quantity. Please try again.');
    }
  };

  /**
   * Render individual license item
   */
  const renderLicenseItem = (license: LicenseItem) => {
    const isSelected = selectedForRemoval.has(license.id);
    // Catalog pathways have pk_id; non-catalog (legacy) pathways do not.
    const isCatalogPathway = license.type === 'program' && license.pk_id !== undefined;
    const isLegacyPathway = license.type === 'program' && license.pk_id === undefined;
    const isSelectable = license.type === 'catalog_course' || isCatalogPathway;
    // Skills: use the active-version ID for the link and display name lookup
    const displayId = license.activeVersionId || license.id;
    const hasVersions = license.type === 'catalog_course' && license.versions && license.versions.length > 0;
    const versionsOpen = expandedVersions.has(license.id);
    // Children list — only for pathways
    const hasChildren = license.type === 'program' && license.courseList && license.courseList.length > 0;
    const availableText = license.available === null ? 'Unlimited' : license.available;

    const handleCardClick = (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('button') || target.closest('input') || target.closest('a') || !isSelectable) {
        return;
      }
      toggleLicenseSelection(license.id);
    };

    return (
      <Card
        key={license.id}
        className="mb-3"
        style={{
          ...(isSelected ? { backgroundColor: '#e8f4fd', border: '1px solid #0075b4' } : {}),
          ...(isSelectable ? { cursor: 'pointer' } : {}),
        }}
        onClick={handleCardClick}
      >
        <Card.Body className="p-3">
          <Row className="align-items-center">
            {/* Checkbox — catalog courses only */}
            {isSelectable && (
              <Col xs="auto" className="pr-0">
                <Form.Check
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleLicenseSelection(license.id)}
                  disabled={isRemoving}
                  aria-label={`Select ${license.name} for removal`}
                  onClick={(e) => e.stopPropagation()}
                />
              </Col>
            )}

            {/* Name / title column */}
            <Col xs={8}>
              <div className="d-flex align-items-center">
                {/* Pathway children toggle */}
                {hasChildren && (
                <span
                  role="button"
                  tabIndex={0}
                  className="mr-2 text-primary"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => { e.stopPropagation(); toggleCollapse(license.id); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { toggleCollapse(license.id); } }}
                >
                  {license.isCollapsed ? '▼' : '▲'}
                </span>
                )}
                <div>
                  <h6 className="mb-1">
                    {license.type === 'catalog_course' ? (
                      <Hyperlink
                        destination={getCourseUrl(displayId)}
                        target="_blank"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {displayId}
                      </Hyperlink>
                    ) : (
                      license.name
                    )}
                  </h6>
                  {license.type === 'catalog_course' && getCourseDisplayName(displayId) && (
                    <div className="text-muted" style={{ fontSize: '0.875rem', fontWeight: 'normal' }}>
                      {getCourseDisplayName(displayId)}
                    </div>
                  )}
                  <small className="text-muted">
                    <Badge variant={license.type === 'legacy_bundle' ? 'primary' : 'secondary'}>
                      {license.type === 'legacy_bundle' ? 'Legacy Bundle' : 'Skill'}
                    </Badge>
                  </small>
                </div>
              </div>
            </Col>

            {/* Available / Used — side by side, centered */}
            {!isLegacyPathway && (
              <Col className="d-flex justify-content-center">
                <div className="d-flex align-items-center" style={{ gap: '1.25rem' }}>
                  <div className="text-center">
                    <strong>{availableText}</strong>
                    <br />
                    <small className="text-muted">Available</small>
                  </div>
                  <div className="text-center">
                    <strong>{license.used}</strong>
                    <br />
                    <small className="text-muted">Used</small>
                  </div>
                </div>
              </Col>
            )}
            {isLegacyPathway && (
              <Col className="d-flex justify-content-center">
                <small className="text-muted">Not yet in catalog</small>
              </Col>
            )}
          </Row>

          {/* Action buttons row — catalog items only */}
          {(license.type === 'catalog_course' || isCatalogPathway) && (
            <Row className="mt-2">
              <Col className="d-flex justify-content-start" style={{ gap: '0.5rem', marginLeft: '2rem' }}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setEditingLicense(license);
                    setEditQuantity('');
                    setShowEditModal(true);
                  }}
                  disabled={isUpdating}
                >
                  Edit
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleRemoveLicense(license)}
                  disabled={isRemoving}
                >
                  Remove
                </Button>
              </Col>
            </Row>
          )}

          {/* Versions panel — skills only */}
          {/* Version toggle link — skills only */}
          {hasVersions && (
            <div className="mt-1 ml-4">
              <Button
                variant="link"
                size="sm"
                className="p-0"
                style={{ fontSize: '0.8rem' }}
                onClick={(e) => { e.stopPropagation(); toggleVersionsExpanded(license.id); }}
              >
                {versionsOpen ? 'Hide versions' : `Show versions (${license.versions!.length})`}
              </Button>
            </div>
          )}
          {hasVersions && versionsOpen && (
            <div className="pl-4 mt-3 border-top pt-2">
              <h6 className="mb-2 text-muted" style={{ fontSize: '0.85rem' }}>Versions</h6>
              {license.versions!.map(v => (
                <Row key={v.version} className="mb-1 align-items-center">
                  <Col xs={6}>
                    <small>
                      <Hyperlink
                        destination={getCourseUrl(license.id.endsWith('+') ? `${license.id}${v.version}` : license.id)}
                        target="_blank"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {license.id.endsWith('+') ? `${license.id}${v.version}` : v.version}
                      </Hyperlink>
                      {v.active && (
                        <Badge variant="info" className="ml-2" style={{ fontSize: '0.7rem' }}>Active</Badge>
                      )}
                    </small>
                  </Col>
                  <Col xs={3} className="text-center">
                    <small>{v.used}</small>
                    <br />
                    <small className="text-muted">Used</small>
                  </Col>
                </Row>
              ))}
            </div>
          )}

          {/* Pathway children — collapsible */}
          {hasChildren && (
            <Collapsible
              styling="card-lg"
              className="mt-3"
              open={!license.isCollapsed}
              onClick={(e) => { e.stopPropagation(); toggleCollapse(license.id); }}
            >
              <div className="pl-4">
                <h6 className="mb-2">Pathway Courses:</h6>
                {license.courseList?.map((courseId) => (
                  <Row key={courseId} className="mb-2 align-items-center">
                    <Col xs={10}>
                      <div>
                        <Hyperlink
                          destination={getCourseUrl(courseId)}
                          target="_blank"
                        >
                          {courseId}
                        </Hyperlink>
                        {getCourseDisplayName(courseId) && (
                          <>
                            <br />
                            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                              {getCourseDisplayName(courseId)}
                            </span>
                          </>
                        )}
                        <br />
                        <small className="text-muted">Edit this skill individually from the skills list above</small>
                      </div>
                    </Col>
                  </Row>
                ))}
              </div>
            </Collapsible>
          )}
        </Card.Body>
      </Card>
    );
  };

  if (!company) {
    return (
      <Card>
        <Card.Body>
          <p className="text-muted text-center">Select a company to manage licenses</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="license-management">
      <Card.Body className="px-4">
        {isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Loading licenses...</p>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center pt-3 mb-3">
              <div>
                <strong>Total Licenses: {filteredLicenses.length}</strong>
              </div>
              {licenses.filter(license => license.type === 'legacy_bundle').length <= 0 && (
                <div className="d-flex" style={{ gap: 4 }}>
                  <Button
                    variant="danger"
                    onClick={() => setShowRemoveConfirmModal(true)}
                    disabled={selectedForRemoval.size === 0 || isRemoving}
                  >
                    {selectedForRemoval.size === 0
                      ? 'Remove Licenses'
                      : `Remove ${selectedForRemoval.size} License${selectedForRemoval.size > 1 ? 's' : ''}`}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      setShowAddModal(true);
                      setSelectedCourses([]);
                      setCourseSearchQuery('');
                      setShowCourseDropdown(false);
                    }}
                    disabled={isAdding || isLoading}
                  >
                    Add Licenses
                  </Button>
                </div>
              )}
            </div>
            {/* Operation Error Alert */}
            {operationError && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => setOperationError('')}
                className="mb-3"
              >
                {operationError}
              </Alert>
            )}

            {/* Search */}
            <Row className="mb-3">
              <Col xs={12} md={6}>
                <SearchField
                  placeholder="Search licenses..."
                  value={searchTerm}
                  onSubmit={setSearchTerm}
                  onChange={setSearchTerm}
                />
              </Col>
            </Row>

            {/* License List */}
            {filteredLicenses.length === 0 ? (
              <Alert variant="info">
                {searchTerm ? 'No licenses match your search criteria.' : 'No licenses found for this company.'}
              </Alert>
            ) : (
              <div className="license-list-container pt-3 mb-3" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {filteredLicenses.map(renderLicenseItem)}
              </div>
            )}
          </>
        )}
      </Card.Body>

      {/* Add License Modal */}
      <ModalDialog
        title="Add New License"
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setModalError('');
          setModalSuccess('');
          setSelectedCourses([]);
          setCourseSearchQuery('');
          setShowCourseDropdown(false);
        }}
        size="xl"
      >
        <ModalDialog.Header>
          <ModalDialog.Title>
            Add New License
          </ModalDialog.Title>
        </ModalDialog.Header>

        <ModalDialog.Body style={{ height: '50vh', overflowY: 'auto' }}>
          {modalError && <Alert variant="danger">{modalError}</Alert>}
          {modalSuccess && <Alert variant="success">{modalSuccess}</Alert>}

          {/* Course Filter Options */}
          <Form.Group className="mb-3">
            <Form.Label>Filter Courses</Form.Label>
            <Row>
              <Col xs={6}>
                <Form.Check
                  type="radio"
                  id="filter-all"
                  name="courseFilter"
                  label="Show all courses"
                  checked={courseFilter === 'all'}
                  onChange={() => setCourseFilter('all')}
                />
              </Col>
              <Col xs={6}>
                <Form.Check
                  type="radio"
                  id="filter-company"
                  name="courseFilter"
                  label={`Show only ${company?.friendly_name || 'company'} courses`}
                  checked={courseFilter === 'company'}
                  onChange={() => setCourseFilter('company')}
                  disabled={!companyOrg}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <Form.Check
                  type="radio"
                  id="filter-uopx"
                  name="courseFilter"
                  label="Show only UOPX courses"
                  checked={courseFilter === 'uopx'}
                  onChange={() => setCourseFilter('uopx')}
                />
              </Col>
              <Col xs={6}>
                <Form.Check
                  type="radio"
                  id="filter-both"
                  name="courseFilter"
                  label={`Show ${company?.friendly_name || 'company'} and UOPX courses`}
                  checked={courseFilter === 'both'}
                  onChange={() => setCourseFilter('both')}
                  disabled={!companyOrg}
                />
              </Col>
            </Row>
          </Form.Group>

          <Form.Group>
            <Form.Label>Course List</Form.Label>
            <div className="position-relative">
              <Form.Control
                type="text"
                value={courseSearchQuery}
                onChange={(e) => {
                  setCourseSearchQuery(e.target.value);
                  setShowCourseDropdown(e.target.value.trim().length > 0);
                }}
                placeholder="Search by course ID or name..."
                disabled={isLoadingCourses}
              />

              {showCourseDropdown && courseSearchQuery.trim() && (
                <div
                  className="position-absolute w-100 border rounded bg-white shadow-sm"
                  style={{
                    maxHeight: '450px', overflowY: 'auto', zIndex: 1000, top: '100%', marginTop: '4px',
                  }}
                >
                  {filterCoursesByOrg(courses)
                    .filter(course => !selectedCourses.some(sc => sc.courseId === course.courseId)
                      && (course.courseId.toLowerCase().includes(courseSearchQuery.toLowerCase())
                       || course.displayName.toLowerCase().includes(courseSearchQuery.toLowerCase())))
                    .slice(0, 10)
                    .map(course => (
                      <div
                        key={course.courseId}
                        className="p-2 cursor-pointer"
                        style={{ cursor: 'pointer' }}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          setSelectedCourses([...selectedCourses, { courseId: course.courseId, quantity: null }]);
                          // Only close dropdown if Ctrl/Cmd is not held
                          if (!e.ctrlKey && !e.metaKey) {
                            setCourseSearchQuery('');
                            setShowCourseDropdown(false);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            setSelectedCourses([...selectedCourses, { courseId: course.courseId, quantity: null }]);
                          }
                        }}
                      >
                        <strong>{course.courseId}</strong> - {course.displayName}
                      </div>
                    ))}
                  {courses.filter(course => !selectedCourses.some(sc => sc.courseId === course.courseId)
                    && (course.courseId.toLowerCase().includes(courseSearchQuery.toLowerCase())
                     || course.displayName.toLowerCase().includes(courseSearchQuery.toLowerCase()))).length === 0 && (
                     <div className="p-2 text-muted">No courses found</div>
                  )}
                </div>
              )}
            </div>
            <Form.Text className="text-muted">
              Search by course ID or name. <strong>Tip:</strong> Ctrl+click selects multiple courses.
            </Form.Text>
          </Form.Group>

          {selectedCourses.length > 0 && (
            <div className="mt-3">
              <Form.Label>Selected Courses</Form.Label>
              {selectedCourses.map((selectedCourse) => {
                const course = courses.find(c => c.courseId === selectedCourse.courseId);
                return (
                  <Row key={selectedCourse.courseId} className="mb-2 align-items-center border rounded p-2 bg-light">
                    <Col xs={9}>
                      <div>
                        <strong>{selectedCourse.courseId}</strong>
                        {course && <div><small className="text-muted">{course.displayName}</small></div>}
                      </div>
                    </Col>
                    <Col xs={2}>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={selectedCourse.quantity !== null ? selectedCourse.quantity : ''}
                        onChange={(e) => {
                          const newQuantity = e.target.value === '' ? null : parseInt(e.target.value, 10);
                          setSelectedCourses(selectedCourses.map(sc => (
                            sc.courseId === selectedCourse.courseId ? { ...sc, quantity: newQuantity } : sc
                          )));
                        }}
                        placeholder="Qty"
                      />
                    </Col>
                    <Col xs={1} className="text-right">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => setSelectedCourses(
                          selectedCourses.filter(sc => sc.courseId !== selectedCourse.courseId),
                        )}
                        iconBefore={Close}
                        className="text-danger p-0"
                      />
                    </Col>
                  </Row>
                );
              })}
              <Form.Text className="text-muted">
                Enter quantity for each course, or leave blank for unlimited.
              </Form.Text>
            </div>
          )}
        </ModalDialog.Body>

        <ModalDialog.Footer>
          <ActionRow>
            <ModalDialog.CloseButton variant="tertiary">
              Cancel
            </ModalDialog.CloseButton>
            <Button
              variant="primary"
              onClick={async () => {
                if (selectedCourses.length === 0) {
                  setModalError('Please select at least one course.');
                  return;
                }

                if (!company) {
                  return;
                }

                const invalidCourse = selectedCourses.find(
                  sc => sc.quantity !== null && sc.quantity < 0,
                );
                if (invalidCourse) {
                  setModalError(`Quantity for "${invalidCourse.courseId}" cannot be less than 0. Leave blank for unlimited.`);
                  return;
                }

                try {
                  setModalError('');

                  // Build a single catalog object with all selected courses
                  const catalogUpdates: { [courseId: string]: { available: number | null } } = {};

                  selectedCourses.forEach(selectedCourse => {
                    catalogUpdates[selectedCourse.courseId] = {
                      available: selectedCourse.quantity,
                    };
                  });

                  // Make a single batched call with all courses
                  const updateData = Object.keys(catalogUpdates).map(courseId => ({
                    course_id: courseId,
                    delta: catalogUpdates[courseId].available,
                  }));

                  await dispatch(updateCompanyLicenseQuantities(
                    company.company_name,
                    updateData,
                    false,
                  ));

                  // Show success message and reset selection
                  setModalSuccess(`Successfully added ${selectedCourses.length} license(s)!`);
                  setSelectedCourses([]);
                  setCourseSearchQuery('');
                  setShowCourseDropdown(false);
                } catch (error) {
                  logStudioHomeError('cmp_licenses_add_failed', error);
                  setModalError('Failed to add licenses. Please try again.');
                }
              }}
              disabled={isUpdating}
            >
              {isUpdating && <Spinner animation="border" size="sm" className="mr-2" />}
              Add Licenses
            </Button>
          </ActionRow>
        </ModalDialog.Footer>
      </ModalDialog>
      {/* Edit License Modal */}
      <ModalDialog
        title={`Edit License: ${editingLicense?.name || ''}`}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingLicense(null);
          setEditQuantity('');
          setModalError('');
        }}
        size="lg"
      >
        <ModalDialog.Header>
          <ModalDialog.Title>
            Edit License: {editingLicense?.name || ''}
          </ModalDialog.Title>
        </ModalDialog.Header>

        <ModalDialog.Body>
          {modalError && <Alert variant="danger">{modalError}</Alert>}

          {editingLicense ? (
            <div>
              <p><strong>License:</strong> {editingLicense.name}</p>
              <p><strong>Type:</strong> {editingLicense.type === 'legacy_bundle' ? 'Legacy Bundle' : 'Catalog Course'}</p>
              <p><strong>Current Available:</strong> {editingLicense.available === null ? 'Unlimited' : editingLicense.available}</p>
              <p><strong>Currently Used:</strong> {editingLicense.used}</p>

              <Form.Group className="mt-3">
                <Form.Label>License Quantity Change</Form.Label>
                <Form.Control
                  type="number"
                  value={editQuantity}
                  onChange={(e) => setEditQuantity(e.target.value)}
                  placeholder="Enter quantity to add (+) or remove (-)"
                />
                <Form.Text className="text-muted">
                  {editingLicense.type === 'legacy_bundle' ? (
                    'Legacy system: Enter positive numbers to add licenses, negative numbers to remove licenses.'
                  ) : (
                    <>Enter positive numbers to add licenses, negative numbers to remove licenses.
                      {editingLicense.available !== null
                        ? ` Current: ${editingLicense.available}. New will: ${editingLicense.available + (parseInt(editQuantity, 10) || 0)}.`
                        : ` Current: Unlimited. New will: ${parseInt(editQuantity, 10) || 0}.`}
                    </>
                  )}
                </Form.Text>
              </Form.Group>

              {editingLicense.type !== 'legacy_bundle' && (
                <div className="mt-2 p-2 bg-light border-left border-primary">
                  <small><strong>Preview:</strong> Current ({editingLicense.available || 'Unlimited'}) → New ({
                    editingLicense.available === null
                      ? 'Unlimited'
                      : (editingLicense.available + (parseInt(editQuantity, 10) || 0))
                  })
                  </small>
                </div>
              )}
            </div>
          ) : (
            <p>No license selected for editing.</p>
          )}
        </ModalDialog.Body>

        <ModalDialog.Footer>
          <ActionRow>
            <ModalDialog.CloseButton variant="tertiary">
              Cancel
            </ModalDialog.CloseButton>
            <Button
              variant="primary"
              onClick={async () => {
                if (!editingLicense) {
                  return;
                }

                const quantityDelta = editQuantity ? parseInt(editQuantity, 10) : null;

                try {
                  await handleUpdateQuantity(editingLicense, quantityDelta);
                } catch (error) {
                  logStudioHomeError('cmp_license_update_failed', error);
                }
              }}
              disabled={isUpdating}
            >
              {isUpdating && <Spinner animation="border" size="sm" className="mr-2" />}
              Update Quantity
            </Button>
          </ActionRow>
        </ModalDialog.Footer>
      </ModalDialog>

      {/* Remove Licenses Confirmation Modal */}
      <ModalDialog
        title="Confirm Removal"
        isOpen={showRemoveConfirmModal}
        onClose={() => setShowRemoveConfirmModal(false)}
        size="md"
      >
        <ModalDialog.Header>
          <ModalDialog.Title>
            Confirm Removal
          </ModalDialog.Title>
        </ModalDialog.Header>

        <ModalDialog.Body>
          <p>
            Are you sure you want to remove <strong>{selectedForRemoval.size}</strong> license
            {selectedForRemoval.size > 1 ? 's' : ''}?
          </p>
          {selectedForRemoval.size > 0 && (
            <div className="mt-3">
              <p className="mb-2"><strong>Licenses to be removed:</strong></p>
              <ul className="list-unstyled" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {Array.from(selectedForRemoval).map(licenseId => {
                  const lic = licenses.find(l => l.id === licenseId);
                  return lic ? (
                    <li key={licenseId} className="mb-1">
                      <Badge variant={lic.type === 'program' ? 'success' : 'secondary'} className="mr-2">
                        {lic.type === 'program' ? 'Pathway' : 'Skill'}
                      </Badge>
                      {lic.name}
                    </li>
                  ) : null;
                })}
              </ul>
            </div>
          )}
          <Alert variant="warning" className="mt-3 mb-0">
            <strong>Warning:</strong> This action cannot be undone.
          </Alert>
        </ModalDialog.Body>

        <ModalDialog.Footer>
          <ActionRow>
            <ModalDialog.CloseButton variant="tertiary">
              Cancel
            </ModalDialog.CloseButton>
            <Button
              variant="danger"
              onClick={handleBatchRemoveLicenses}
              disabled={isRemoving}
            >
              {isRemoving && <Spinner animation="border" size="sm" className="mr-2" />}
              Yes, Remove {selectedForRemoval.size} License{selectedForRemoval.size > 1 ? 's' : ''}
            </Button>
          </ActionRow>
        </ModalDialog.Footer>
      </ModalDialog>
    </Card>
  );
};

export default LicenseManagement;
